import React from 'react';
import { FormikProps } from 'formik';
import { DesignFormModel } from '../../../../designs/design-form/designFormModel';
import { UnitFloorHeatLossFieldValue, UnitHeatLossFieldValue } from '../../../../designs/DesignFormDefaultsContext';
import { ManifoldFormModel } from '../../manifolds/manifoldFormModel';
import { DownlossTypeCode } from '../../rooms/heatLoss/downlossTypeCode';

export const onUnitWallTemperatureChange = (
  newValue: string,
  unitIndex: number,
  wallIndex: number,
  formProps: FormikProps<DesignFormModel>,
  unitWallElements: Array<UnitHeatLossFieldValue>,
  setUnitWallElements: React.Dispatch<React.SetStateAction<Array<UnitHeatLossFieldValue>>>,
  providedNewManifoldValues: Array<ManifoldFormModel> | null = null,
  setManifoldFieldValues = true,
) => {
  if (unitWallElements[wallIndex] == undefined || unitWallElements[wallIndex].value === newValue) {
    return;
  }

  const newUnitWallElements = [...unitWallElements];
  const manifoldValues = unitWallElements[wallIndex].manifoldRoomValues ?? [];
  const unitManifolds =
    providedNewManifoldValues != null && providedNewManifoldValues.length !== 0
      ? providedNewManifoldValues
      : (formProps.values.units[unitIndex].manifolds as Array<ManifoldFormModel>);
  const newManifoldValues: Array<ManifoldFormModel> = providedNewManifoldValues ?? [];

  for (let manifoldIndex = 0; manifoldIndex < manifoldValues.length; manifoldIndex++) {
    const rooms = [...(unitManifolds[manifoldIndex].rooms ?? [])];
    for (let roomIndex = 0; roomIndex < manifoldValues[manifoldIndex].length; roomIndex++) {
      if (manifoldValues[manifoldIndex][roomIndex] === unitWallElements[wallIndex].value) {
        manifoldValues[manifoldIndex][roomIndex] = newValue;
        newUnitWallElements[wallIndex].manifoldRoomValues = manifoldValues;

        const roomHeatLossDetails = rooms?.[roomIndex].heatLossDetails;
        const walls = roomHeatLossDetails?.walls;

        if (rooms[roomIndex].heatLossDetails == null || roomHeatLossDetails == null || walls == null) {
          continue;
        }

        const newWalls = [...walls];
        newWalls[wallIndex] = { ...walls[wallIndex], externalAirTemperatureInDegreesCelsius: newValue };
        rooms[roomIndex] = {
          ...rooms[roomIndex],
          heatLossDetails: {
            ...roomHeatLossDetails,
            walls: newWalls,
          },
        };
      }
    }
    newManifoldValues[manifoldIndex] = {
      ...unitManifolds[manifoldIndex],
      rooms,
    };
  }

  newUnitWallElements[wallIndex].value = newValue;
  setUnitWallElements(newUnitWallElements);
  if (setManifoldFieldValues) {
    formProps.setFieldValue(`units.${unitIndex}.manifolds`, newManifoldValues);
  }
};

export const onUnitRoofTemperatureChange = (
  newValue: string,
  unitIndex: number,
  roofIndex: number,
  formProps: FormikProps<DesignFormModel>,
  unitRoofElements: Array<UnitHeatLossFieldValue>,
  setUnitRoofElements: React.Dispatch<React.SetStateAction<Array<UnitHeatLossFieldValue>>>,
  providedNewManifoldValues: Array<ManifoldFormModel> | null = null,
  setManifoldFieldValues = true,
) => {
  if (unitRoofElements[roofIndex] == undefined || unitRoofElements[roofIndex].value === newValue) {
    return;
  }

  const newUnitRoofElements = [...unitRoofElements];
  const manifoldValues = unitRoofElements[roofIndex].manifoldRoomValues ?? [];
  const unitManifolds =
    providedNewManifoldValues != null && providedNewManifoldValues.length !== 0
      ? providedNewManifoldValues
      : (formProps.values.units[unitIndex].manifolds as Array<ManifoldFormModel>);
  const newManifoldValues: Array<ManifoldFormModel> = providedNewManifoldValues ?? [];

  for (let manifoldIndex = 0; manifoldIndex < manifoldValues.length; manifoldIndex++) {
    const rooms = [...(unitManifolds[manifoldIndex].rooms ?? [])];
    for (let roomIndex = 0; roomIndex < manifoldValues[manifoldIndex].length; roomIndex++) {
      if (manifoldValues[manifoldIndex][roomIndex] === unitRoofElements[roofIndex].value) {
        manifoldValues[manifoldIndex][roomIndex] = newValue;
        newUnitRoofElements[roofIndex].manifoldRoomValues = manifoldValues;

        const roomHeatLossDetails = rooms?.[roomIndex].heatLossDetails;
        const roofs = roomHeatLossDetails?.roofs;

        if (rooms[roomIndex].heatLossDetails == null || roomHeatLossDetails == null || roofs == null) {
          continue;
        }

        const newRoofs = [...roofs];
        newRoofs[roofIndex] = { ...roofs[roofIndex], temperatureAboveInDegreesCelsius: newValue };
        rooms[roomIndex] = {
          ...rooms[roomIndex],
          heatLossDetails: {
            ...roomHeatLossDetails,
            roofs: newRoofs,
          },
        };
      }
    }
    newManifoldValues[manifoldIndex] = {
      ...unitManifolds[manifoldIndex],
      rooms,
    };
  }

  newUnitRoofElements[roofIndex].value = newValue;
  setUnitRoofElements(newUnitRoofElements);
  if (setManifoldFieldValues) {
    formProps.setFieldValue(`units.${unitIndex}.manifolds`, newManifoldValues);
  }
};

export const onUnitFloorTemperatureChange = (
  newValue: string,
  unitIndex: number,
  floorIndex: number,
  formProps: FormikProps<DesignFormModel>,
  unitFloorElements: Array<UnitFloorHeatLossFieldValue>,
  setUnitFloorElements: React.Dispatch<React.SetStateAction<Array<UnitFloorHeatLossFieldValue>>>,
  newDownlossTypeCode: DownlossTypeCode | null = null,
  providedNewManifoldValues: Array<ManifoldFormModel> | null = null,
  setManifoldFieldValues = true,
) => {
  if (
    unitFloorElements[floorIndex] == undefined ||
    (unitFloorElements[floorIndex].temperature === newValue &&
      (newDownlossTypeCode == null || newDownlossTypeCode === unitFloorElements[floorIndex].downlossTypeCode))
  ) {
    return;
  }

  const newUnitFloorElements = [...unitFloorElements];
  const manifoldValues = unitFloorElements[floorIndex].manifoldRoomValues ?? [];
  const unitManifolds =
    providedNewManifoldValues != null && providedNewManifoldValues.length !== 0
      ? providedNewManifoldValues
      : (formProps.values.units[unitIndex].manifolds as Array<ManifoldFormModel>);
  const newManifoldValues: Array<ManifoldFormModel> = providedNewManifoldValues ?? [];

  for (let manifoldIndex = 0; manifoldIndex < manifoldValues.length; manifoldIndex++) {
    const rooms = [...(unitManifolds[manifoldIndex].rooms ?? [])];
    for (let roomIndex = 0; roomIndex < manifoldValues[manifoldIndex].length; roomIndex++) {
      let isUpdated = false;
      if (
        newDownlossTypeCode != null &&
        manifoldValues[manifoldIndex][roomIndex].downlossTypeCode === unitFloorElements[floorIndex].downlossTypeCode
      ) {
        manifoldValues[manifoldIndex][roomIndex].downlossTypeCode = newDownlossTypeCode;
        isUpdated = true;

        if (manifoldValues[manifoldIndex][roomIndex].temperature === unitFloorElements[floorIndex].temperature) {
          manifoldValues[manifoldIndex][roomIndex].temperature = newValue;
        }
      } else if (
        newDownlossTypeCode == null &&
        manifoldValues[manifoldIndex][roomIndex].temperature === unitFloorElements[floorIndex].temperature
      ) {
        manifoldValues[manifoldIndex][roomIndex].temperature = newValue;
        isUpdated = true;
      }

      if (isUpdated) {
        newUnitFloorElements[floorIndex].manifoldRoomValues = manifoldValues;
        const roomValues = manifoldValues[manifoldIndex][roomIndex];

        const roomHeatLossDetails = rooms?.[roomIndex].heatLossDetails;
        const floors = roomHeatLossDetails?.floors;

        if (roomHeatLossDetails == null || floors == null) {
          continue;
        }

        const newFloors = [...floors];
        newFloors[floorIndex] = {
          ...floors[floorIndex],
          downlossTypeCode: roomValues.downlossTypeCode,
          temperatureBelowInDegreesCelsius: roomValues.temperature,
        };
        rooms[roomIndex] = {
          ...rooms[roomIndex],
          heatLossDetails: {
            ...roomHeatLossDetails,
            floors: newFloors,
          },
        };
      }
    }
    newManifoldValues[manifoldIndex] = {
      ...unitManifolds[manifoldIndex],
      rooms,
    };
  }

  newUnitFloorElements[floorIndex].downlossTypeCode =
    newDownlossTypeCode ?? unitFloorElements[floorIndex].downlossTypeCode;
  newUnitFloorElements[floorIndex].temperature = newValue;
  setUnitFloorElements(newUnitFloorElements);
  if (setManifoldFieldValues) {
    formProps.setFieldValue(`units.${unitIndex}.manifolds`, newManifoldValues);
  }
};
